import { useCallback, useEffect, useState } from 'react';

import _ from 'lodash';

import { TEMPLATE_ERROR_STATUS, TemplateRequestStatus } from '../FormChangeHistory.constants';
import { getTemplateRequestErrorStatus } from '../FormChangeHistory.utils';

/**
 * Runs `request` (a stable thunk resolving to the data) when it changes, on `retry`, or when
 * `sharedRetryCount` (a retry made by another reader of the same data) changes; a null request reads
 * nothing. The status is loading until the current request and attempt answer, so a response to an
 * earlier one never shows. A failure's status comes from `errorStatuses`, by HTTP code. Data that
 * answers after its request was replaced or its reader unmounted goes to `onDiscard`, so a reader
 * holding resources (a pdf.js document) can release it.
 */
const useTemplateRequest = (
  request,
  sharedRetryCount = 0,
  errorStatuses = TEMPLATE_ERROR_STATUS,
  onDiscard = null,
) => {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState(null);

  const retry = useCallback(() => setAttempt(previous => previous + 1), []);

  useEffect(() => {
    if (!request) {
      return _.noop;
    }

    const subscription = { isActive: true };

    request()
      .then(data => {
        if (!subscription.isActive) {
          onDiscard?.(data);

          return;
        }

        setResult({
          request,
          attempt,
          sharedRetryCount,
          status: TemplateRequestStatus.LOADED,
          data,
        });
      })
      .catch(error => {
        if (subscription.isActive) {
          setResult({
            request,
            attempt,
            sharedRetryCount,
            status: getTemplateRequestErrorStatus(error, errorStatuses),
            data: null,
          });
        }
      });

    return () => {
      subscription.isActive = false;
    };
  }, [request, attempt, sharedRetryCount, errorStatuses, onDiscard]);

  const isCurrent =
    !!result &&
    result.request === request &&
    result.attempt === attempt &&
    result.sharedRetryCount === sharedRetryCount;

  return {
    status: isCurrent ? result.status : TemplateRequestStatus.LOADING,
    data: isCurrent ? result.data : null,
    retry,
  };
};

export default useTemplateRequest;
